import { formatPercent } from '../../formatters'
import { barWidth } from './bars'

// How tall a connector row (the rate between two stages) is against a stage
// row, for the height budget the stylesheet shares out.
export const CONNECTOR_WEIGHT = 0.6

// A connector is drawn before an explicit rate step, and before a total that
// owns a `rate`, even a null one: null is a rate that is not tracked.
export function hasConnector(step) {
  return step.kind === 'rate' || Object.hasOwn(step, 'rate')
}

// How tall a stage's bar row is against its label row when a narrow card
// stacks each stage (label and number on one line, the bar under them).
export const STACKED_BAR_WEIGHT = 0.6

// The rows the card's height is shared between: a stage counts one (one and
// STACKED_BAR_WEIGHT when stacked), a connector CONNECTOR_WEIGHT. Never under
// one, so an empty list divides by something.
export function funnelSlots(steps, { stacked = false } = {}) {
  const stage = stacked ? 1 + STACKED_BAR_WEIGHT : 1
  const slots = steps.reduce((sum, step) => sum + (step.kind === 'total' ? stage : 0) + (hasConnector(step) ? CONNECTOR_WEIGHT : 0), 0)
  return Math.max(1, Math.round(slots * 100) / 100)
}

// The largest stage, which every bar is drawn against; 0 when no stage has a
// number.
export function funnelMax(steps) {
  return steps.reduce((max, step) => (step.kind === 'total' && Number.isFinite(step.count) ? Math.max(max, step.count) : max), 0)
}

// The CSS width of a stage's bar. A stage with nothing (zero, or no number)
// draws an empty track rather than the sliver a share bar keeps for a little.
export function funnelFillWidth(count, max) {
  if (!Number.isFinite(count) || count <= 0 || !(max > 0)) return '0%'
  return barWidth(count, max)
}

// The nearest stage before `index`, which an implicit rate is a share of.
export function previousTotal(steps, index) {
  for (let at = index - 1; at >= 0; at -= 1) {
    if (steps[at].kind === 'total') return steps[at]
  }
  return null
}

// What the connector before `step` says: the rate, its wording and whether
// it is tracked. An explicit rate step brings its own; a total's rate is
// worded as a share of the stage before it. Null when there is no connector.
export function connectorFor(step, previous) {
  if (!hasConnector(step)) return null
  if (step.kind === 'rate') {
    const note = step.note ?? null
    return {
      value: step.value,
      label: step.label,
      shortLabel: step.shortLabel ?? step.label,
      state: step.state ?? 'tracked',
      note,
      title: `${step.label}: ${step.value}${note ? ` (${note})` : ''}`,
    }
  }
  if (!Number.isFinite(step.rate)) {
    return { value: 'Not tracked', label: null, shortLabel: null, state: 'untracked', note: null, title: `${step.label}: not tracked` }
  }
  const value = formatPercent(step.rate)
  return {
    value,
    label: previous ? `of ${previous.label}` : null,
    shortLabel: null,
    state: 'tracked',
    note: null,
    title: previous ? `${step.label}: ${value} of ${previous.label}` : `${step.label}: ${value}`,
  }
}
